import {useEffect, useRef, useState} from "react";
import Header from "./Header";
import Loading from "./Loading";
import {ChevronDown, Check, ExternalLink} from "lucide-react";
import {useNavigate} from "react-router-dom";
import {fetchSimilarPapers} from "../service/ManuscriptService";

function SimilarWorks() {
    const navigate = useNavigate();
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");

    const [loading, setLoading] = useState(true);

    // Store all papers
    const [papers, setPapers] = useState([]);

    // Categories ("ALL" + dynamic sections)
    const [categories, setCategories] = useState([]);

    // Default category = ALL
    const [searchBy, setSearchBy] = useState("ALL");

    const [searchOpen, setSearchOpen] = useState(false);
    const searchRef = useRef(null);

    // Capitalize helper
    const cap = (s) =>
        s === "ALL" ? "All" : s.charAt(0).toUpperCase() + s.slice(1);

    // Close dropdown when clicking outside
    useEffect(() => {
        const onDocClick = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target))
                setSearchOpen(false);
        };
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
    }, []);

    const onSearchKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setSearchOpen(o => !o);
        } else if (e.key === "Escape") {
            setSearchOpen(false);
        }
    };

    // Fetch data
    useEffect(() => {
        fetchSimilarPapers(id, true)
            .then(response => {
                const list = response.similarPapers || [];
                setPapers(list);

                const uniqueSections = [...new Set(list.map(p => p.section))];

                // Insert ALL at top
                setCategories(["ALL", ...uniqueSections]);

                setSearchBy("ALL");

                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [id]);

// Filter papers based on dropdown AND sort by similarity descending
    const filtered = (
        searchBy === "ALL"
            ? papers
            : papers.filter(p => p.section === searchBy)
    ).sort((a, b) => b.similarity - a.similarity);


    return (
        <div>
            <Header page="similar-works"/>

            {loading ? (
                <Loading/>
            ) : (
                <div className="body">
                    <div className="heading">Similar Works</div>

                    <div className={"dashboard-row sub-heading"}>
                        <span>Possible Overlaps Found</span>
                    </div>

                    <div className={"export-score-text"}>
                        <span style={{fontWeight: 700, fontSize: 'calc(1em + 2px)'}}>
                          {papers.length}
                        </span>
                        <span> papers show similarity.</span>
                    </div>

                    {/* Dropdown */}
                    <div className={"search-bar-wrapper dashboard-row"}>
                        <div
                            className={"sub-heading"}
                            style={{display: 'inline-block', marginRight: '10px'}}
                        >
                            Category:
                        </div>

                        <div
                            ref={searchRef}
                            className={"search-by-input-half"}
                            role="button"
                            tabIndex={0}
                            aria-haspopup="listbox"
                            aria-expanded={searchOpen}
                            onClick={() => setSearchOpen(o => !o)}
                            onKeyDown={onSearchKeyDown}
                            style={{position: "relative"}}
                        >
                            <div
                                className="dropdown-trigger"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    width: "100%"
                                }}
                            >
                                <span className={"dropdown-text"} style={{right: 0}}>
                                    {cap(searchBy)}
                                </span>

                                <ChevronDown className={`chevron-icon ${searchOpen ? "open" : ""}`}
                                             style={{marginRight: '.5rem'}}/>
                            </div>

                            <ul
                                role="listbox"
                                className={`dropdown-panel dropdown-panel-similar-works ${searchOpen ? "open" : ""}`}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {categories.map(cat => (
                                    <li
                                        key={cat}
                                        role="option"
                                        aria-selected={searchBy === cat}
                                        onClick={() => {
                                            setSearchBy(cat);
                                            setSearchOpen(false);
                                        }}
                                        className="dropdown-option"
                                        style={{
                                            padding: "10px 12px",
                                            cursor: "pointer",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            background: searchBy === cat ? "#f6f6f6" : "transparent"
                                        }}
                                    >
                                        <span style={{textTransform: "capitalize"}}>
                                            {cap(cat)}
                                        </span>

                                        {searchBy === cat && <Check size={16}/>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Results Table */}
                    <div className={"dashboard-row"}>
                        <table className="similar-works-table">
                            <thead>
                            <tr>
                                {/*<th className="similar-works-table-th similar-works-table-th-number">#</th>*/}
                                <th className="similar-works-table-th similar-works-table-th-overlap">Title</th>
                                <th className="similar-works-table-th similar-works-table-th-percentage">% Overlap</th>
                            </tr>
                            </thead>

                            <tbody>
                            {filtered.map((p, i) => (
                                <tr key={p.id}>
                                    {/*<td>{i + 1}</td>*/}
                                    <td className="overflow-ellipsis similar-works-td">
                                        <div className={'similar-works-link-wrapper overflow-ellipsis'}>
                                            <a
                                                href={`https://pubmed.ncbi.nlm.nih.gov/${p.pmid}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="similar-works-link"
                                                style={{textDecoration: 'none', color: 'black'}}
                                            >
                                                {p.articleTitle}
                                            </a>
                                        </div>
                                        <a
                                            href={`https://pubmed.ncbi.nlm.nih.gov/${p.pmid}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="similar-works-link"
                                            style={{textDecoration: 'none', color: 'black'}}
                                        >
                                            <ExternalLink size={30} className={"similar-works-icon"}/>
                                        </a>


                                    </td>
                                    <td>{Math.round(p.similarity * 100)}%</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    <button
                        onClick={() => navigate("/submission?id=" + id)}
                        className={"back-button fade-in margin-bottom-1-rem"}
                    ><span className={"back-button-text"}>&larr;</span>
                    </button>
                </div>
            )}
        </div>
    );
}

export default SimilarWorks;
